import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  BarChart3,
  CandlestickChart,
  ChevronLeft,
  Eye,
  FlaskConical,
  Gauge,
  Globe,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  OctagonX,
  ScrollText,
  Settings,
  ShieldAlert,
  Sun,
  Table2,
  Workflow,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { usePriceAlertToasts } from "@/features/charts/trading";
import { api, errorMessage } from "@/lib/api";
import { useChrome } from "@/lib/chrome";
import { hm, time } from "@/lib/format";
import { useKernel, useMediaQuery, useTheme, useTick } from "@/lib/hooks";
import { useLive } from "@/lib/live";
import { Badge, Button, Dot, Segmented, cn } from "../ui/core";
import { Pnl } from "../ui/data";
import { DropMenu, Sheet, Tip, confirm } from "../ui/overlay";

const NAV = [
  { to: "/", label: "Command center", short: "Home", icon: LayoutDashboard, end: true },
  { to: "/watch", label: "Market watch", short: "Watch", icon: Eye },
  { to: "/charts", label: "Charts", short: "Charts", icon: CandlestickChart },
  { to: "/strategies", label: "Strategies", short: "Strategies", icon: Workflow },
  { to: "/backtests", label: "Backtest lab", short: "Backtests", icon: FlaskConical },
  { to: "/trades", label: "Trades", short: "Trades", icon: Table2 },
  { to: "/options", label: "Option chain", short: "Options", icon: BarChart3 },
  { to: "/markets", label: "Global markets", short: "Markets", icon: Globe },
  { to: "/risk", label: "Risk and limits", short: "Risk", icon: Gauge },
  { to: "/journal", label: "Journal", short: "Journal", icon: ScrollText },
  { to: "/settings", label: "Settings", short: "Settings", icon: Settings },
];

// the phone's tab bar; everything else is under More
// Home, Watch, Charts, Strategies, Trades (five plus More fit a 360 px screen)
const TABS = [NAV[0], NAV[1], NAV[2], NAV[3], NAV[5]];
const MORE = NAV.filter((n) => !TABS.includes(n));

// the phone's top bar: a title per route, and where its back arrow goes
const ROUTES: [RegExp, string, string | null][] = [
  [/^\/$/, "Command center", null],
  [/^\/watch/, "Market watch", null],
  [/^\/charts/, "Charts", null],
  [/^\/strategies\/[^/]+$/, "Strategy", "/strategies"],
  [/^\/strategies/, "Strategies", null],
  [/^\/backtests\/[^/]+$/, "Backtest", "/backtests"],
  [/^\/backtests/, "Backtest lab", null],
  [/^\/trades/, "Trades", null],
  [/^\/options/, "Option chain", null],
  [/^\/markets/, "Global markets", null],
  [/^\/risk/, "Risk and limits", null],
  [/^\/journal/, "Journal", null],
  [/^\/settings\/[^/]+$/, "Settings", "/settings"],
  [/^\/settings/, "Settings", null],
];

function Brand({ compact }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <svg viewBox="0 0 64 64" className="size-7 shrink-0" aria-hidden>
        <rect width="64" height="64" rx="14" fill="var(--panel-3)" />
        <path d="M12 44 L24 32 L33 38 L52 18" fill="none" stroke="var(--up)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="52" cy="18" r="5" fill="var(--accent)" />
      </svg>
      {!compact && <span className="text-[14.5px] font-semibold tracking-tight">AlgoTrader</span>}
    </div>
  );
}

function Nav({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="flex flex-col gap-0.5">
      {NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-medium transition-colors",
              isActive ? "bg-panel-3 text-fg" : "text-muted hover:bg-panel-2 hover:text-fg",
            )
          }
        >
          <Icon className="size-4 shrink-0" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function SystemStatus() {
  const status = useLive((s) => s.status);
  const engine = useLive((s) => s.snapshot?.engine);
  const at = useLive((s) => s.snapshotAt);
  useTick(2000);
  const stale = at > 0 && Date.now() - at > 6000;
  const rows: [string, "up" | "down" | "warn" | "muted", string][] = [
    ["Server", status === "open" && !stale ? "up" : status === "connecting" ? "warn" : "down", status === "open" ? (stale ? "no updates" : "connected") : status],
    ["Engine", engine?.state === "running" ? "up" : engine ? "warn" : "muted", engine?.state ?? "unknown"],
    [
      "Market data",
      engine?.source === "replay" ? "warn" : engine?.feed.connected ? "up" : engine?.session.open ? "down" : "muted",
      engine?.source === "replay" ? "replay" : engine?.feed.connected ? "streaming" : "offline",
    ],
    [
      "Broker",
      engine?.broker.logged_in ? "up" : engine?.broker.configured ? "warn" : "muted",
      engine?.broker.logged_in ? (engine.broker.client_code ?? "logged in") : engine?.broker.configured ? "logged out" : "not set up",
    ],
  ];
  return (
    <div className="space-y-1.5 rounded-xl border border-line bg-panel-2 p-3 text-[12px]">
      {rows.map(([k, tone, v]) => (
        <div key={k} className="flex items-center gap-2">
          <Dot tone={tone} pulse={tone === "up" && k === "Market data"} />
          <span className="text-muted">{k}</span>
          <span className="ml-auto truncate font-medium">{v}</span>
        </div>
      ))}
    </div>
  );
}

function SessionClock() {
  useTick(1000);
  const sess = useLive((s) => s.snapshot?.engine.session);
  const replay = useLive((s) => s.snapshot?.engine.replay);
  const engineTs = useLive((s) => s.snapshot?.engine.ts);
  const now = replay && engineTs ? new Date(engineTs) : new Date();
  let label = "Market closed";
  let tone: "up" | "muted" | "warn" = "muted";
  if (sess?.open) {
    label = `Open, closes ${hm(sess.closes_at)}`;
    tone = "up";
  } else if (sess?.trading_day && sess.opens_at && new Date(sess.opens_at) > now) {
    label = `Opens ${hm(sess.opens_at)}`;
    tone = "warn";
  } else if (sess?.next_open) {
    label = `Next open ${new Date(sess.next_open).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short", day: "2-digit", month: "short" })}`;
  }
  return (
    <div className="flex items-center gap-2">
      <span className="mono text-[13px] font-medium">{time(now.toISOString())}</span>
      <span className="text-[11px] text-faint">IST</span>
      <Badge tone={tone === "up" ? "up" : tone === "warn" ? "warn" : "muted"}>
        {tone === "up" && <Dot tone="up" pulse />}
        {replay ? `Replay ${replay.speed}x, ${label.toLowerCase()}` : label}
      </Badge>
    </div>
  );
}

function KillSwitch() {
  const { k, mode } = useKernel();
  const engaged = k ? !k.risk.trading_enabled : false;
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (body: { on: boolean; flatten: boolean; mode?: string }) => api.post("/api/kill-switch", body),
    onSuccess: (_, v) => {
      // the switch is a setting: pages holding a copy (Risk) must see it now
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success(v.on ? "Kill switch engaged: no new entries" : "Trading re-enabled");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (engaged)
    return (
      <Tip content="New entries are blocked. Releasing needs your password.">
        <Button size="sm" variant="outline" className="h-8 border-down text-down" onClick={() => m.mutate({ on: false, flatten: false, mode })} loading={m.isPending} icon={<ShieldAlert className="size-4" />} aria-label="Trading halted: release">
          <span className="hidden md:inline">Halted</span>
        </Button>
      </Tip>
    );
  return (
    <DropMenu
      trigger={
        <Button size="sm" variant="danger" className="h-8 max-md:w-8 max-md:px-0" icon={<OctagonX className="size-4" />} loading={m.isPending} aria-label="Kill switch">
          <span className="hidden md:inline">Kill switch</span>
        </Button>
      }
      items={[
        {
          label: "Stop new entries (positions keep their exits)",
          onSelect: async () => {
            if (await confirm({ title: "Engage the kill switch?", body: "Strategies stop opening positions. Open positions keep their stops, targets and square-off.", confirm: "Stop new entries", danger: true }))
              m.mutate({ on: true, flatten: false });
          },
        },
        {
          label: "Stop and close every position",
          danger: true,
          onSelect: async () => {
            if (await confirm({ title: "Close everything now?", body: "Blocks new entries and sends exit orders for every open position, in PAPER and LIVE.", confirm: "Close everything", danger: true, requireText: "CLOSE" }))
              m.mutate({ on: true, flatten: true });
          },
        },
      ]}
    />
  );
}

function ModeSwitch() {
  const view = useLive((s) => s.view);
  const setView = useLive((s) => s.setView);
  const { hasLive } = useKernel();
  return (
    <Segmented
      size="xs"
      value={hasLive ? view : "PAPER"}
      onChange={(v) => (hasLive ? setView(v) : toast.info("LIVE appears once your Angel One account is connected in Settings"))}
      options={[
        { value: "PAPER", label: "Paper" },
        { value: "LIVE", label: "Live", tone: "live" },
      ]}
    />
  );
}

/** The phone's top bar: brand or back, the title once the page's own title has
 * scrolled away, today's P&L, Paper or Live, and the kill switch. */
function MobileBar() {
  const loc = useLocation();
  const nav = useNavigate();
  const { k } = useKernel();
  const pageTitle = useChrome((s) => s.title);
  const onScreen = useChrome((s) => s.titleOnScreen);
  const route = ROUTES.find(([re]) => re.test(loc.pathname));
  const back = route?.[2] ?? null;
  const title = pageTitle ?? route?.[1] ?? "AlgoTrader";
  const showTitle = !onScreen;
  return (
    <header className="safe-top shrink-0 border-b border-line bg-panel/95 backdrop-blur lg:hidden">
      <div className="flex h-12 items-center gap-1.5 px-2">
        {back ? (
          <button onClick={() => nav(back)} className="-ml-0.5 grid size-10 shrink-0 place-items-center rounded-full active:bg-panel-3" aria-label="Back">
            <ChevronLeft className="size-5" />
          </button>
        ) : (
          <div className="shrink-0 pl-1">
            <Brand compact />
          </div>
        )}
        <div className={cn("min-w-0 flex-1 truncate px-1 text-[16px] font-semibold tracking-tight transition-opacity duration-200", showTitle ? "opacity-100" : "opacity-0")}>{title}</div>
        {k && loc.pathname !== "/" && (
          <button onClick={() => nav("/")} className="shrink-0 rounded-lg px-1.5 py-1 text-[13px] font-semibold active:bg-panel-3" aria-label="Today's P&L">
            <Pnl value={k.pnl.total} flash={false} />
          </button>
        )}
        <ModeSwitch />
        <KillSwitch />
      </div>
    </header>
  );
}

function TabBar({ onMore }: { onMore: () => void }) {
  const loc = useLocation();
  const inMore = MORE.some((n) => loc.pathname.startsWith(n.to));
  const item = (active: boolean, Icon: typeof Menu, label: string) => (
    <>
      <span className={cn("grid h-7 w-12 place-items-center rounded-full transition-colors", active && "bg-accent-soft")}>
        <Icon className="size-[19px]" />
      </span>
      <span className={cn("text-[10.5px]", active ? "font-semibold" : "font-medium")}>{label}</span>
    </>
  );
  return (
    <nav className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-line bg-panel/95 backdrop-blur lg:hidden">
      <div className="flex h-[58px]">
        {TABS.map(({ to, short, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => cn("flex flex-1 flex-col items-center justify-center gap-0.5", isActive ? "text-accent" : "text-muted")}>
            {({ isActive }) => item(isActive, Icon, short)}
          </NavLink>
        ))}
        <button onClick={onMore} className={cn("flex flex-1 flex-col items-center justify-center gap-0.5", inMore ? "text-accent" : "text-muted")}>
          {item(inMore, Menu, "More")}
        </button>
      </div>
    </nav>
  );
}

export function AppShell({ children, user, onSignOut }: { children: ReactNode; user: string; onSignOut: () => void }) {
  const [more, setMore] = useState(false);
  const [theme, toggleTheme] = useTheme();
  const { k, mode } = useKernel();
  const loc = useLocation();
  const nav = useNavigate();
  const phone = useMediaQuery("(max-width: 767px)");
  const fullBleed = loc.pathname.startsWith("/charts") || loc.pathname.startsWith("/markets");
  // a phone's full chart takes the whole screen, like a native chart app; its list of light charts is a normal page
  const search = new URLSearchParams(loc.search);
  const fullScreen = phone && ((loc.pathname === "/charts" && search.has("key")) || (loc.pathname === "/markets" && search.has("symbol")));
  const chartList = phone && (loc.pathname === "/charts" || loc.pathname === "/markets") && !fullScreen;
  const padded = !fullBleed || chartList;
  usePriceAlertToasts();
  return (
    <div className="flex h-full">
      <aside className="hidden w-[232px] shrink-0 flex-col border-r border-line bg-panel px-3 py-3.5 lg:flex">
        <div className="mb-5 px-1.5">
          <Brand />
        </div>
        <Nav />
        <div className="mt-auto space-y-3">
          <SystemStatus />
          <div className="flex items-center gap-1 px-1">
            <span className="min-w-0 flex-1 truncate text-[12px] text-muted">{user}</span>
            <Tip content={theme === "dark" ? "Light theme" : "Dark theme"}>
              <Button size="icon-sm" variant="ghost" onClick={toggleTheme} aria-label="Toggle theme">
                {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
              </Button>
            </Tip>
            <Tip content="Sign out">
              <Button size="icon-sm" variant="ghost" onClick={onSignOut} aria-label="Sign out">
                <LogOut className="size-4" />
              </Button>
            </Tip>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {!fullScreen && <MobileBar />}
        <header className={cn("hidden h-13 shrink-0 items-center gap-3 border-b border-line bg-panel/80 px-4 backdrop-blur lg:flex", mode === "LIVE" && "border-b-live/50")}>
          <SessionClock />
          <div className="ml-auto flex items-center gap-3">
            {k && (
              <button onClick={() => nav("/")} className="flex items-baseline gap-1.5" title="Today's net P&L (realized plus open)">
                <span className="text-[11px] text-muted uppercase">Today</span>
                <Pnl value={k.pnl.total} className="text-[14px] font-semibold" />
              </button>
            )}
            <ModeSwitch />
            <KillSwitch />
          </div>
        </header>
        {mode === "LIVE" && !fullScreen && (
          <div className="flex h-6 shrink-0 items-center justify-center gap-2 bg-live-soft text-[11.5px] font-medium text-live">
            <Activity className="size-3.5" /> Viewing LIVE: real orders, real money
          </div>
        )}
        <main
          className={cn(
            "min-h-0 flex-1 overflow-y-auto overscroll-y-contain",
            padded && "px-4 pt-3 sm:px-5 sm:pt-5",
            // room for the phone's tab bar
            fullScreen ? "" : padded ? "pb-[calc(70px+env(safe-area-inset-bottom))] lg:pb-6" : "pb-[calc(58px+env(safe-area-inset-bottom))] lg:pb-0",
          )}
        >
          {padded ? <div className="mx-auto max-w-[1500px]">{children}</div> : children}
        </main>
      </div>

      {!fullScreen && <TabBar onMore={() => setMore(true)} />}
      <Sheet open={more} onOpenChange={setMore} side="bottom" title="More">
        <div className="grid grid-cols-3 gap-2">
          {MORE.map(({ to, short, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMore(false)}
              className={({ isActive }) =>
                cn("flex flex-col items-center gap-1.5 rounded-2xl border px-2 py-3.5 text-[12.5px] font-medium transition-colors active:scale-[0.98]", isActive ? "border-accent bg-accent-soft text-accent" : "border-line bg-panel-2 text-fg")
              }
            >
              <Icon className="size-5" />
              {short}
            </NavLink>
          ))}
        </div>
        <div className="mt-4">
          <SystemStatus />
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[13px] text-muted">Signed in as {user}</span>
          <Button size="sm" variant="secondary" onClick={toggleTheme} icon={theme === "dark" ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}>
            {theme === "dark" ? "Light" : "Dark"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onSignOut} icon={<LogOut className="size-3.5" />}>
            Sign out
          </Button>
        </div>
      </Sheet>
    </div>
  );
}
